import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, Wrench } from "lucide-react";
import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/button";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { company } from "@/content/site";
import { peopleGroups, peopleWorkflow, publicTeamProfiles } from "@/content/people";

export default function People() {
  return (
    <>
      <Seo
        title={`People & shop | ${company.name}`}
        description="Meet the dispatch, driver, safety, office, and maintenance shop teams behind Bull and Bear Co tanker freight."
      />

      <section className="relative overflow-hidden bg-brand-900 text-white">
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.06]"
          style={{ backgroundImage: "repeating-linear-gradient(45deg, #fff 0 1px, transparent 1px 10px)" }}
        />
        <div className="pointer-events-none absolute -right-40 -top-40 h-[480px] w-[480px] rounded-full bg-accent-500/15 blur-3xl" />
        <div className="relative mx-auto grid max-w-[1200px] items-center gap-10 px-6 py-20 lg:grid-cols-[1fr_0.9fr] lg:py-24">
          <div>
            <div className="mb-3 text-sm font-semibold uppercase tracking-wider text-accent-400">People & shop</div>
            <h1 className="mb-5 max-w-3xl text-[clamp(2.25rem,4.5vw,3.5rem)] font-bold leading-[1.05]">
              Real people behind every BB load.
            </h1>
            <p className="max-w-2xl text-lg text-brand-200">
              Freight does not move because of one person. It takes dispatchers,
              drivers, owner-operators, safety, accounting, office support, and the
              maintenance shop working together.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/careers" className={cn(buttonVariants({ variant: "accent", size: "lg" }), "w-full justify-center sm:w-auto")}>
                Join the team <ArrowRight className="ml-1 h-4 w-4" />
              </Link>
              <Link
                to="/contact"
                className={cn(
                  buttonVariants({ variant: "outline", size: "lg" }),
                  "w-full justify-center border-white/30 bg-transparent text-white hover:bg-white/10 hover:text-white sm:w-auto",
                )}
              >
                Work with BB
              </Link>
            </div>
          </div>

          <div className="relative">
            <div className="overflow-hidden rounded-2xl border border-white/10 bg-white/10 shadow-2xl">
              <img
                src="/brand/careers/drivers.jpg"
                alt="Bull and Bear road team with tanker equipment"
                className="h-[360px] w-full object-cover"
              />
            </div>
            <div className="absolute -bottom-5 left-5 right-5 rounded-xl border border-white/10 bg-brand-950/90 p-4 text-sm text-brand-100 shadow-xl backdrop-blur">
              <div className="mb-1 flex items-center gap-2 font-semibold text-white">
                <CheckCircle2 className="h-4 w-4 text-accent-300" />
                People-first operation
              </div>
              Dispatch, road, safety, office, and shop teams supporting one freight promise.
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-24">
        <div className="mx-auto max-w-[1200px] px-6">
          <Reveal className="mb-12 max-w-3xl">
            <div className="mb-3 text-sm font-semibold uppercase tracking-wider text-accent-500">Featured people</div>
            <h2 className="text-3xl text-brand-900 lg:text-4xl">Names, titles, and the people customers will recognize.</h2>
            <p className="mt-4 text-lg text-brand-600">
              This is where BB can publish approved headshots, names, roles, and short
              bios for leadership, dispatch, safety, accounting, and shop team members.
            </p>
          </Reveal>

          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-5">
            {publicTeamProfiles.map((person, index) => (
              <Reveal key={`${person.department}-${person.title}`} delay={index * 80} className="h-full">
                <article className="flex h-full flex-col items-center rounded-[2rem] border border-brand-100 bg-white p-6 text-center shadow-sm">
                  <div className="relative mb-5">
                    <div className="h-36 w-36 overflow-hidden rounded-full border-4 border-white bg-brand-100 shadow-lg ring-1 ring-brand-100">
                      <img src={person.image} alt={person.imageAlt} loading="lazy" className="h-full w-full object-cover" />
                    </div>
                    <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-brand-900 px-3 py-1 text-[0.68rem] font-semibold uppercase tracking-wide text-white shadow-sm">
                      {person.department}
                    </div>
                  </div>
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-accent-600">{person.title}</p>
                  <h3 className="text-xl font-semibold text-brand-900">{person.name}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-brand-600">{person.bio}</p>
                  <div className="mt-5 w-full border-t border-brand-100 pt-4 text-left">
                    <p className="mb-3 text-center text-xs font-semibold uppercase tracking-wide text-brand-400">Helps with</p>
                    <ul className="grid gap-2 text-sm text-brand-700">
                      {person.helpsWith.map((item) => (
                        <li key={item} className="flex items-start gap-2">
                          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-accent-500" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-24">
        <div className="mx-auto max-w-[1200px] px-6">
          <Reveal className="mb-12 max-w-3xl">
            <div className="mb-3 text-sm font-semibold uppercase tracking-wider text-accent-500">Our teams</div>
            <h2 className="text-3xl text-brand-900 lg:text-4xl">Different roles, one operation.</h2>
            <p className="mt-4 text-lg text-brand-600">
              Each group has its own job, but the goal is shared: clear communication,
              road-ready equipment, safe paperwork, and dependable freight movement.
            </p>
          </Reveal>

          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-5">
            {peopleGroups.map((group, index) => (
              <Reveal key={group.title} delay={index * 80} className="h-full">
                <article className="h-full overflow-hidden rounded-xl border border-brand-100 bg-white shadow-sm">
                  <div className="relative aspect-[4/3] overflow-hidden bg-brand-100">
                    <img src={group.image} alt={group.imageAlt} loading="lazy" className="h-full w-full object-cover transition duration-500 hover:scale-105" />
                    <div className="absolute left-4 top-4 inline-flex h-10 w-10 items-center justify-center rounded-lg bg-brand-900/85 text-white shadow-sm">
                      <group.icon className="h-5 w-5" />
                    </div>
                  </div>
                  <div className="p-6">
                    <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-brand-400">{group.kicker}</p>
                    <h3 className="mb-2 text-lg font-semibold text-brand-900">{group.title}</h3>
                    <p className="text-sm text-brand-600">{group.body}</p>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-brand-50 py-20 lg:py-24">
        <div className="mx-auto grid max-w-[1200px] items-center gap-10 px-6 lg:grid-cols-[1fr_1.1fr]">
          <Reveal>
            <div className="mb-3 text-sm font-semibold uppercase tracking-wider text-accent-500">Maintenance shop</div>
            <h2 className="mb-5 text-3xl text-brand-900 lg:text-4xl">
              The shop deserves to be seen.
            </h2>
            <p className="mb-6 text-lg text-brand-600">
              The maintenance shop supports the trucks, tankers, inspections, and
              repair flow that keep equipment ready for dispatch. It is part of how
              BB keeps loads moving instead of waiting on preventable equipment issues.
            </p>
            <ul className="grid gap-3 text-sm text-brand-700 sm:grid-cols-2">
              {[
                "Tractor and tanker readiness",
                "Inspection and repair support",
                "Maintenance communication with operations",
                "Equipment visibility for staff workflows",
              ].map((item) => (
                <li key={item} className="flex items-start gap-2 rounded-lg bg-white p-3">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-accent-500" />
                  {item}
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={120}>
            <div className="grid gap-4 sm:grid-cols-[0.9fr_1.1fr]">
              <div className="overflow-hidden rounded-xl border border-brand-100 bg-white shadow-sm">
                <img
                  src="/brand/industries/manufacturing.jpg"
                  alt="Maintenance shop equipment readiness"
                  loading="lazy"
                  className="h-full min-h-[280px] w-full object-cover"
                />
              </div>
              <div className="relative overflow-hidden rounded-xl border border-brand-100 bg-brand-900 p-6 text-white shadow-sm">
                <div className="absolute -right-16 -top-16 h-44 w-44 rounded-full bg-accent-500/20 blur-3xl" />
                <Wrench className="relative mb-8 h-12 w-12 text-accent-300" />
                <blockquote className="relative text-2xl font-semibold leading-snug">
                  “The road team can only perform when the equipment is ready. The shop
                  helps make that possible.”
                </blockquote>
                <p className="relative mt-4 text-sm text-brand-300">
                  Maintenance, dispatch, and safety all support the same operational promise.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="py-20 lg:py-24">
        <div className="mx-auto max-w-[1200px] px-6">
          <Reveal className="mb-10 max-w-2xl">
            <div className="mb-3 text-sm font-semibold uppercase tracking-wider text-accent-500">How it connects</div>
            <h2 className="text-3xl text-brand-900 lg:text-4xl">People, paperwork, equipment, freight.</h2>
          </Reveal>
          <div className="grid gap-5 md:grid-cols-3">
            {peopleWorkflow.map((item, index) => (
              <Reveal key={item.title} delay={index * 80} className="h-full">
                <article className="h-full rounded-xl border border-brand-100 p-6">
                  <item.icon className="mb-4 h-7 w-7 text-accent-500" />
                  <h3 className="mb-2 font-semibold text-brand-900">{item.title}</h3>
                  <p className="text-sm text-brand-600">{item.body}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-brand-900 py-16 text-white">
        <Reveal className="mx-auto flex max-w-[1200px] flex-col items-center justify-between gap-6 px-6 md:flex-row">
          <div>
            <h2 className="text-3xl text-white lg:text-4xl">Want to work with the team?</h2>
            <p className="mt-2 max-w-xl text-brand-300">
              Talk to BB about freight, driving opportunities, owner-operator onboarding,
              or office/shop roles as they open.
            </p>
          </div>
          <div className="flex w-full flex-wrap gap-3 md:w-auto">
            <Link to="/careers" className={cn(buttonVariants({ variant: "accent", size: "lg" }), "w-full justify-center sm:w-auto")}>
              See careers <ArrowRight className="ml-1 h-4 w-4" />
            </Link>
            <Link
              to="/contact"
              className={cn(
                buttonVariants({ variant: "outline", size: "lg" }),
                "w-full justify-center border-white/30 bg-transparent text-white hover:bg-white/10 hover:text-white sm:w-auto",
              )}
            >
              Contact us
            </Link>
          </div>
        </Reveal>
      </section>
    </>
  );
}
